import React, { useMemo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { fetchAssignmentList } from "../assignmentSlice";
import { 
  retrieveCommentaryPDFAPI, 
  retrieveVocabularyRetryPDFAPI,
  retrieveVocabularyWordbookPDFAPI 
} from "../assignmentAPI";

import { formatDate, formatFullDateString, formatScore } from "../../../utils/textUtils";
import { refinePages } from "../../../utils/paginationUtils";
import { rowOrderOf } from "../../leveltest/rowOrder";

import { typeInfo, bookBadgeOf } from "../../../constants/typeInfo";
import Pagination from "../../../components/Pagination";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import BookIcon from "@mui/icons-material/Book";
import FactCheckIcon from "@mui/icons-material/FactCheckOutlined";
import secret from "../../../secret.json";
import { readSessionFromUrl } from "../../leveltest/session";
import PaperAssignmentModal from "./PaperAssignmentModal";
import LevelTestResultList from "../../leveltest/components/LevelTestResultList";
import ReviewList from "../../leveltest/components/ReviewList";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";

/* ── ECHO 톤 태그 색상 ── */
const TAG_STYLES = {
  vocabulary: { bg: "#E9EFF7", color: "#3E6398" },
  sentence: { bg: "#FBF4D9", color: "#8A6D00" },
  analysis: { bg: "#E8F7EE", color: "#1DB05B" },
  grammar: { bg: "#EFEBFC", color: "#6B4EE6" },
  comprehensive: { bg: "#FDF3E3", color: "#E8920A" },
  paragraph: { bg: "#FCEDE9", color: "#C2452D" },
  paper_assignment: { bg: "#F4F8F7", color: "#3A434E" },
  class_content: { bg: "#F4F8F7", color: "#3A434E" },
};
// 여기 없는 종류(수학 등)는 typeInfo 의 색을 쓴다. 교사 화면(WorksheetTypeBox)과 같은 색이다
const getTagStyle = (type) =>
  TAG_STYLES[type] ||
  (typeInfo[type] ? { bg: typeInfo[type].backgroundColor, color: typeInfo[type].color } : { bg: "#F4F8F7", color: "#3A434E" });

/* ── 점수 칩 색상 ── */
const getScoreStyle = (score) => {
  if (score === null || score === undefined) return { bg: "#F4F8F7", color: "#7A8490" };
  if (score >= 80) return { bg: "#E5FBF8", color: "#00A396" };
  if (score >= 60) return { bg: "#F4F8F7", color: "#3A434E" };
  return { bg: "#FCEDE9", color: "#C2452D" };
};

// 채점 해설지(동그라미 · 엑스, 서버 api/assignment/english_pdf.py)를 여는 영어 과제 종류. 서버 WORKSHEET_TYPE_NAME 과 같다
const ENGLISH_EXPLAIN_PDF_TYPE_LIST = ["paragraph", "grammar", "comprehensive", "sentence", "vocabulary", "bank", "grammar_bank", "analysis", "readers_book"];

const iconBtnStyle = {
  width: Math.min(window.innerWidth, window.screen.width) <= 768 ? "30px" : "34px",
  height: Math.min(window.innerWidth, window.screen.width) <= 768 ? "30px" : "34px",
  borderRadius: "10px",
  border: "1.5px solid #E6EDEC",
  backgroundColor: "#FFF",
  color: "#3A434E",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flex: "0 0 auto",
  padding: 0,
};

const FinishedAssignmentTable = () => {
  const dispatch = useDispatch();
  const [paperModalOpen, setPaperModalOpen] = useState(false);
  const [selectedPaperAssignment, setSelectedPaperAssignment] = useState(null);

  const { assignmentList, current_page, total_pages } = useSelector(
    (state) => state.assignment
  );
  const { student } = useSelector((state) => state.student);
  const { mode } = useSelector((state) => state.ui);
  const isMobile = mode === "mobile";

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  const showCommentaryPDF = async (assignmentId) => {
    const pdfWindow = window.open();
    const response = await retrieveCommentaryPDFAPI(assignmentId, student.id);
    if (response.status !== 200) alert("해설지를 불러오는데 실패했습니다.");

    const file = new Blob([response.data], {
      type: "application/pdf",
    });
    const fileURL = URL.createObjectURL(file);
    pdfWindow.location.href = fileURL;
  };

  // 서버가 누를 때 굽는다. 카카오톡 안 브라우저에서도 열리게 주소로 바로 연다
  const showExplainPDF = (assignmentId) => {
    const identity = readSessionFromUrl();
    if (!identity) return;
    window.open(
      `${secret.API_BASE_URL.replace(/\/+$/, "")}/api/v1/assignment/english/explain-pdf?assignment_id=${assignmentId}` +
        `&student_id=${identity.studentId}&random_code=${identity.randomCode}`,
      "_blank"
    );
  };

  const showReviewPage = (assignmentId) => {
    const queryParams = new URLSearchParams(window.location.search);
    const session = queryParams.get("session");
    const reviewURL = `/review?id=${assignmentId}&session=${session}&type=student`;

    const reviewWindow = window.open();
    reviewWindow.location.href = reviewURL;
  };

  const showVocabularyRetryPDF = async (assignmentId, retryType) => {
    try {
      const pdfWindow = window.open();
      const response = await retrieveVocabularyRetryPDFAPI(assignmentId, student.id, retryType);
      
      if (response.status !== 200) {
        alert(`${retryType === 'wordbook' ? '단어장' : '재시험지'}를 불러오는데 실패했습니다.`);
        pdfWindow.close();
        return;
      }

      const file = new Blob([response.data], {
        type: "application/pdf",
      });
      const fileURL = URL.createObjectURL(file);
      pdfWindow.location.href = fileURL;
    } catch (error) {
      console.error("Error loading vocabulary retry PDF:", error);
      alert(`${retryType === 'wordbook' ? '단어장' : '재시험지'}를 불러오는데 실패했습니다.`);
    }
  };

  const showVocabularyWordbookPDF = async (assignmentId) => {
    try {
      const pdfWindow = window.open();
      const response = await retrieveVocabularyWordbookPDFAPI(assignmentId, student.id);
      
      if (response.status !== 200) {
        alert("단어장을 불러오는데 실패했습니다.");
        pdfWindow.close();
        return;
      }

      const file = new Blob([response.data], {
        type: "application/pdf",
      });
      const fileURL = URL.createObjectURL(file);
      pdfWindow.location.href = fileURL;
    } catch (error) {
      console.error("Error loading vocabulary wordbook PDF:", error);
      alert("단어장을 불러오는데 실패했습니다.");
    }
  };

  const handlePageClick = (page) => {
    dispatch(
      fetchAssignmentList({
        studentId: student.id,
        page,
        isFinished: true,
      })
    );
  };

  const handlePaperAssignmentClick = (assignment) => {
    setSelectedPaperAssignment(assignment);
    setPaperModalOpen(true);
  };

  const handlePaperModalClose = () => {
    setPaperModalOpen(false);
    setSelectedPaperAssignment(null);
  };

  useEffect(() => {
    if (student.id)
      dispatch(
        fetchAssignmentList({ studentId: student.id, isFinished: true })
      );
  }, [dispatch, student.id]);

  /* 제출시간: 짧은 날짜 + 마우스 올리면 전체 시간 툴팁 */
  const getSubmittedInfo = (assignment) => {
    let dateString = null;
    if (assignment.type === 'class_content') {
      dateString = assignment.created_at;
    } else if (assignment.type === 'paper_assignment') {
      dateString = assignment.paper_assignment_data?.student_submission?.submitted_at || null;
    } else {
      dateString = assignment.finished_at;
    }
    if (!dateString) return null;
    return {
      short: `${formatDate(dateString)} 제출`,
      full: formatFullDateString({ dateString, defaultString: "" }),
    };
  };

  return (
    <div style={{ maxWidth: `${CONTAINER_MAX_WIDTH}px`, margin: "0 auto", padding: isMobile ? "0 12px" : "0 22px" }}>
      {/* 페이지 헤더 */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: isMobile ? "16px 0 12px" : "26px 0 18px" }}>
        <h3 style={{ fontSize: isMobile ? "1.05rem" : "1.22rem", fontWeight: 800, color: "#0E141B", margin: 0 }}>
          완료 과제 목록
        </h3>
      </div>

      {/* 행 카드 */}

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {/* 레벨테스트 응시와 복습. ECHO 계통이라 과제와 별개지만 목록에서는 같이
            보여준다. 응시(점수·리포트)가 먼저고 그 뒤에 복습이 온다. */}
        <LevelTestResultList variant="finished" />
        <ReviewList variant="finished" />

        {assignmentList && assignmentList.length > 0 ? (
          assignmentList.map((assignment, index) => {
            const tagStyle = getTagStyle(assignment.type);
            const tagLabel = assignment.type === 'class_content'
              ? assignment.class_content_data?.typeName
              : typeInfo[assignment.type]?.text;
            const submitted = getSubmittedInfo(assignment);

            const scoreValue = assignment.type === 'class_content'
              ? assignment.score
              : assignment.type === 'paper_assignment'
                ? assignment.score
                : assignment.grade;
            const scoreStyle = getScoreStyle(scoreValue);
            const scoreText = assignment.type === 'class_content'
              ? (assignment.score !== null ? `${formatScore(assignment.score)}/${formatScore(assignment.totalScore || 100)}` : '점수 없음')
              : assignment.type === 'paper_assignment'
                ? (assignment.score !== null ? `${formatScore(assignment.score)}점` : '채점 중')
                : `${assignment.grade}점`;

            const isClickableScore = assignment.type !== 'class_content';

            return (
              <div
                key={index}
                style={{
                  // 레벨테스트·복습 줄과 한 줄기로 섞이도록 날짜로 자리를 매긴다.
                  order: rowOrderOf(
                    assignment.finished_at || assignment.submitted_at || assignment.created_at
                  ),
                  backgroundColor: "#FFF",
                  border: "1px solid #E6EDEC",
                  borderRadius: "16px",
                  padding: isMobile ? "12px 14px" : "15px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "10px" : "14px",
                  boxShadow: "0 10px 30px -24px rgba(20,40,50,.2)",
                  flexWrap: "nowrap", // [정렬 v1] 모바일 2줄 방지
                }}
              >
                {/* 날짜 */}
                {!isMobile && (
                  <div style={{ flex: "0 0 auto", width: "96px", lineHeight: 1.5 }}>
                    <div style={{ fontSize: "0.8rem", color: "#7A8490", fontWeight: 600 }}>
                      {formatDate(assignment.created_at)} 출제
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#3A434E", fontWeight: 700 }}>
                      <em style={{ fontStyle: "normal", fontSize: "0.7rem", color: "#B9C1C9", fontWeight: 700, marginRight: "4px" }}>마감</em>
                      {assignment.closed_at ? formatDate(assignment.closed_at) : "-"}
                    </div>
                  </div>
                )}

                {/* 제목 + 태그 */}
                {/* [정렬 v1] 제목 맨 앞 + 1줄 고정 — 버튼/배지가 제목 공간을 먹어
                    "6모 대비 수능 동사 2000개"가 2줄로 꺾이던 것 해소 */}
                <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "7px", flexWrap: "nowrap", overflow: "hidden" }}>
                  <span style={{
                    fontWeight: 700,
                    fontSize: isMobile ? "0.88rem" : "0.95rem",
                    color: "#0E141B",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}>
                    {assignment.title}
                  </span>
                  {assignment.is_retry && (
                    <span style={{ fontSize: "0.7rem", fontWeight: 800, backgroundColor: "#E8F7EE", color: "#158A48", padding: "3px 9px", borderRadius: "7px", flex: "0 0 auto", whiteSpace: "nowrap" }}>
                      복습 {assignment.retry_info?.retry_count || ''}회차
                    </span>
                  )}
                  {tagLabel && (
                    <span style={{ fontSize: "0.73rem", fontWeight: 700, padding: "3px 10px", borderRadius: "999px", backgroundColor: tagStyle.bg, color: tagStyle.color, flex: "0 0 auto", whiteSpace: "nowrap", ...(bookBadgeOf(assignment.type, assignment.title)?.style || {}) }}>
                      {bookBadgeOf(assignment.type, assignment.title)?.text || tagLabel}
                    </span>
                  )}
                </div>


                {/* 해설지/첨삭 아이콘 */}
                <div style={{ display: "flex", gap: "6px", alignItems: "center", flex: "0 0 auto" }}>
                  {assignment.type === 'class_content' ? null : assignment.type === 'paper_assignment' ? (
                    assignment.paper_assignment_data?.student_submission?.feedback_file_name ? (
                      <button
                        onClick={() => handlePaperAssignmentClick(assignment)}
                        style={iconBtnStyle}
                        title="첨삭 파일 보기"
                      >
                        <DescriptionIcon style={{ fontSize: "20px" }} />
                      </button>
                    ) : null
                  ) : (
                    <>
                      {assignment.commentary_pdf_path !== "" && (
                        <button
                          onClick={() => showCommentaryPDF(assignment.id)}
                          style={iconBtnStyle}
                          title="해설지 보기"
                        >
                          <DescriptionIcon style={{ fontSize: "20px" }} />
                        </button>
                      )}
                      {ENGLISH_EXPLAIN_PDF_TYPE_LIST.includes(assignment.type) && (
                        <button
                          onClick={() => showExplainPDF(assignment.id)}
                          style={iconBtnStyle}
                          title="채점 해설지 보기"
                        >
                          <FactCheckIcon style={{ fontSize: "20px" }} />
                        </button>
                      )}
                      {assignment.type === "vocabulary" && assignment.grade !== 100 && (
                        <>
                          <button
                            style={iconBtnStyle}
                            onClick={() => showVocabularyRetryPDF(assignment.id, "test")}
                            title="단어 재시험지 PDF 다운로드"
                          >
                            <RefreshIcon style={{ fontSize: "20px" }} />
                          </button>
                          <button
                            style={iconBtnStyle}
                            onClick={() => showVocabularyWordbookPDF(assignment.id)}
                            title="틀린 단어 단어장 PDF 다운로드"
                          >
                            <BookIcon style={{ fontSize: "20px" }} />
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* 점수 칩 (클릭 시 학습내역) — 아이콘 버튼(0~3개 가변) 뒤, 제출시간 앞. 제출시간이 고정폭(92px)이라 점수 열이 세로 정렬됨 */}
                <button
                  style={{
                    flex: "0 0 auto",
                    minWidth: isMobile ? "58px" : "70px", // 점수 열 폭 고정 — 정렬 유지
                    textAlign: "center",
                    fontWeight: 800,
                    fontSize: "0.92rem",
                    padding: "6px 16px",
                    borderRadius: "999px",
                    border: "none",
                    backgroundColor: scoreStyle.bg,
                    color: scoreStyle.color,
                    fontVariantNumeric: "tabular-nums",
                    cursor: isClickableScore ? "pointer" : "default",
                  }}
                  title={isClickableScore ? "학습내역 보기" : undefined}
                  onClick={() => {
                    if (assignment.type === 'class_content') return;
                    if (assignment.type === 'paper_assignment') {
                      handlePaperAssignmentClick(assignment);
                    } else {
                      showReviewPage(assignment.id);
                    }
                  }}
                >
                  {scoreText}
                </button>

                {/* 제출 시간 - 짧게 표시, hover 시 전체 시간 */}
                {!isMobile && (
                  <span
                    style={{
                      flex: "0 0 auto",
                      width: "92px",
                      textAlign: "right",
                      fontSize: "0.82rem",
                      color: "#7A8490",
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {submitted && (
                      <span
                        style={{
                          borderBottom: "1px dashed #B9C1C9",
                          paddingBottom: "1px",
                          cursor: "help",
                        }}
                        title={submitted.full}
                      >
                        {submitted.short}
                      </span>
                    )}
                  </span>
                )}

              </div>
            );
          })
        ) : (
          <div
            style={{
              border: "1.5px dashed #E6EDEC",
              borderRadius: "16px",
              padding: "40px 18px",
              textAlign: "center",
              color: "#7A8490",
              fontSize: "0.9rem",
              backgroundColor: "#FFF",
            }}
          >
            아직 완료한 과제가 없어요.
          </div>
        )}
      </div>

      <Pagination
        visiblePages={visiblePages}
        current_page={current_page}
        handlePageClick={handlePageClick}
      />

      <PaperAssignmentModal
        isOpen={paperModalOpen}
        onClose={handlePaperModalClose}
        assignment={selectedPaperAssignment}
      />
    </div>
  );
};

export default FinishedAssignmentTable;
